import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Uploader from "@/components/Uploader";
import MediaView from "@/components/MediaView";
import Linkify from "@/components/Linkify";
import ReportButton from "@/components/ReportButton";
import { Quote, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Testimonies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/testimonies");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setBusy(true);
    try {
      await api.post("/testimonies", { title, content, image_path: imagePath });
      setTitle(""); setContent(""); setImagePath(null); setOpen(false);
      load();
      toast.success("Testimony shared");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Could not share testimony");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-burgundy">Testimony Wall</div>
          <h1 className="font-serif text-4xl text-jjblue mt-2">They overcame by the word of their testimony</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)]">Healings, restorations, breakthroughs—write them here so the next generation remembers.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(!open)} data-testid="new-testimony-btn">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Share testimony
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="jj-card p-6 mt-6" data-testid="new-testimony-form">
          <input className="input-jj" placeholder="Title (e.g. 'He restored what was lost')"
            value={title} onChange={(e) => setTitle(e.target.value)} data-testid="t-title" />
          <textarea rows={5} className="input-jj mt-3" placeholder="Tell what He did…"
            value={content} onChange={(e) => setContent(e.target.value)} data-testid="t-content" />
          <div className="mt-4 flex items-center justify-between">
            <Uploader accept="image/*" label={imagePath ? "Image attached" : "Add image (optional)"}
              onUploaded={(d) => setImagePath(d.path)} testId="t-upload" />
            <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="t-submit">{busy ? "Sharing…" : "Share"}</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {items.length === 0 && <div className="text-center py-10 text-[var(--jj-muted)] col-span-full">No testimonies yet. Start the wall.</div>}
        {items.map((t) => {
          const canDel = user?.id === t.author?.id || user?.role === "admin";
          const remove = async () => {
            if (!window.confirm("Delete this testimony?")) return;
            try {
              await api.delete(`/testimonies/${t.id}`);
              setItems(items.filter((x) => x.id !== t.id));
              toast.success("Testimony deleted");
            } catch { toast.error("Could not delete"); }
          };
          return (
          <article key={t.id} className="jj-card overflow-hidden flex flex-col relative" data-testid={`testimony-${t.id}`}>
            {canDel && (
              <button onClick={remove} aria-label="Delete testimony"
                className="absolute top-3 right-3 z-10 text-[var(--jj-muted)] hover:text-burgundy bg-white/80 rounded-full p-1.5"
                data-testid={`testimony-delete-${t.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
            {t.image_path && <MediaView path={t.image_path} className="w-full h-48 object-cover" />}
            <div className="p-7">
              <Quote className="h-6 w-6 text-gold" strokeWidth={1.4} />
              <h3 className="font-serif text-2xl text-jjblue mt-3">{t.title}</h3>
              <p className="mt-4 text-jjblue/85 leading-relaxed"><Linkify>{t.content}</Linkify></p>
              <div className="mt-5 flex items-center justify-between text-xs text-[var(--jj-muted)]">
                <span>— {t.author?.name} · {new Date(t.created_at).toLocaleDateString()}</span>
                <ReportButton targetType="testimony" targetId={t.id} label="" />
              </div>
            </div>
          </article>
          );
        })}
      </div>
    </div>
  );
}
